/**
 * Candidate self-cancellation of a viewing (PUT /api/appointments/{id}/sign-out) with the late-
 * cancellation handshake, shared by My Viewings, the property page and both tour components.
 *
 * Cancelling on the viewing's own day at or after 07:00 (Europe/Amsterdam) bans the subscription.
 * The server enforces that: the first request changes nothing and answers 409
 * `late_confirmation_required` with the translated warning; only a second request that carries
 * `confirm_late: 1` - sent after the candidate explicitly confirms the warning here - cancels and
 * bans. Anyone bypassing this page gets the same 409 until they send the flag themselves.
 *
 * Resolves to the final response data ({status, banned?, message?}) when the viewing was
 * cancelled, to null when the candidate chose to keep it; rejects on any other failure.
 *
 * @param {number|string} appointmentId
 * @param {{http: {put: Function}, sweetalert: Function}} deps axios-like client and sweetalert
 * @param {Object} [params] extra request fields (for example { redirect: 1 })
 */
export function signOutViewing(appointmentId, deps, params = {}) {
  const url = '/api/appointments/' + appointmentId + '/sign-out';

  return deps.http.put(url, params).then(
    (response) => response.data || {},
    (error) => {
      const data = error && error.response && error.response.data;
      if (!error || !error.response || error.response.status !== 409 || !data || data.status !== 'late_confirmation_required') {
        throw error;
      }

      return Promise.resolve(deps.sweetalert(data.title || '', {
        text: data.message || '',
        icon: 'warning',
        dangerMode: true,
        buttons: {
          cancel: { text: data.cancel_button || 'Cancel', value: false, visible: true },
          confirm: { text: data.confirm_button || 'OK', value: true },
        },
      })).then((confirmed) => {
        if (!confirmed) {
          return null;
        }
        return deps.http.put(url, Object.assign({}, params, { confirm_late: 1 })).then((response) => response.data || {});
      });
    }
  );
}

/** The "cancelled and blocked" notice a late cancellation shows once, before the page reloads. */
export function showBannedNotice(result, deps, title) {
  if (result && result.banned && result.message) {
    return Promise.resolve(deps.sweetalert(title, result.message, 'warning'));
  }
  return Promise.resolve();
}
